import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../config/axios';
import mqtt from 'mqtt';
import '../styles/DeviceControl.css';

function DeviceControl() {
  const { deviceId } = useParams();
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [messages, setMessages] = useState([]);
  const [mqttClient, setMqttClient] = useState(null);

  useEffect(() => {
    fetchDevice();
    const mqttTimer = setTimeout(() => {
      connectMQTT();
    }, 500);

    const poll = setInterval(() => {
      fetchDevice();
    }, 5000);

    return () => {
      clearTimeout(mqttTimer);
      clearInterval(poll);
      if (mqttClient) {
        console.log('🔌 Disconnecting MQTT client...');
        mqttClient.end();
      }
    };
  }, [deviceId]);

  // Re-subscribe when device is loaded
  useEffect(() => {
    if (mqttClient && device?.mqttTopic && mqttClient.connected) {
      console.log('📡 Subscribing to device topic:', device.mqttTopic);
      mqttClient.subscribe(device.mqttTopic, (err) => {
        if (err) {
          console.error('❌ Subscription error:', err);
        } else {
          console.log(`✅ Subscribed to ${device.mqttTopic}`);
        }
      });
    }
  }, [device, mqttClient]);

  const fetchDevice = async () => {
    try {
      const response = await axios.get(`/devices/${deviceId}`);
      setDevice(response.data);
    } catch (error) {
      console.error('Error fetching device:', error);
    }
  };

  const connectMQTT = () => {
    // IMPORTANT: React environment variables must start with REACT_APP_
    // Use WSS (secure WebSocket) for HTTPS pages - required for security
    // Use nginx proxy path /mqtt instead of direct port 9001
    const DEFAULT_MQTT_URL = 'wss://srv878795.hstgr.cloud/mqtt';
    const envUrl = process.env.REACT_APP_MQTT_WS_URL;
    
    // Determine final URL - reject any localhost URLs
    let finalUrl = envUrl || DEFAULT_MQTT_URL;
    
    // CRITICAL: Force reject localhost - this should NEVER happen
    if (finalUrl && (finalUrl.includes('localhost') || finalUrl.includes('127.0.0.1'))) {
      console.error('🚨 CRITICAL ERROR: Detected localhost in MQTT URL!');
      console.error('🚨 Original URL:', finalUrl);
      console.error('🚨 Environment variable:', envUrl);
      finalUrl = DEFAULT_MQTT_URL;
      console.log('✅ Using safe fallback URL:', finalUrl);
    }
    
    // Final validation - ensure we have a valid URL
    if (!finalUrl || finalUrl.trim() === '') {
      finalUrl = DEFAULT_MQTT_URL;
    }
    
    // Final validation
    if (!finalUrl.startsWith('ws://') && !finalUrl.startsWith('wss://')) {
      console.error('🚨 Invalid WebSocket protocol, using default');
      finalUrl = DEFAULT_MQTT_URL;
    }
    
    // Force WSS for HTTPS pages (security requirement - Mixed Content prevention)
    if (window.location.protocol === 'https:' && finalUrl.startsWith('ws://')) {
      console.warn('⚠️ HTTPS page detected, converting ws:// to wss:// for security');
      finalUrl = finalUrl.replace('ws://', 'wss://');
    }
    
    // ABSOLUTE SAFETY: Hardcode to prevent any localhost usage
    if (finalUrl.includes('localhost') || finalUrl.includes('127.0.0.1')) {
      console.error('🚨🚨🚨 FINAL SAFETY CHECK: Still detected localhost! Forcing to production URL.');
      finalUrl = 'wss://srv878795.hstgr.cloud/mqtt';
    }
    
    console.log('═══════════════════════════════════════');
    console.log('🔌 MQTT WebSocket Connection Setup');
    console.log('═══════════════════════════════════════');
    console.log('📍 Final URL:', finalUrl);
    console.log('🔍 Env Variable:', envUrl || '(not set)');
    console.log('🔍 Default URL:', DEFAULT_MQTT_URL);
    console.log('✅ URL Validation: PASSED');
    console.log('═══════════════════════════════════════');
    
    // Create connection with explicit URL validation
    const connectionUrl = finalUrl.trim();
    
    // Determine protocol from URL
    const protocol = connectionUrl.startsWith('wss://') ? 'wss' : 'ws';
    
    // Extract path from URL (e.g., /mqtt from wss://srv878795.hstgr.cloud/mqtt)
    const urlObj = new URL(connectionUrl);
    const path = urlObj.pathname || '/mqtt';
    
    const client = mqtt.connect(connectionUrl, {
      reconnectPeriod: 5000,
      connectTimeout: 10000,
      keepalive: 60,
      clean: true,
      // Use secure protocol for HTTPS pages
      protocol: protocol,
      protocolVersion: 4,
      // Additional options
      host: urlObj.hostname,
      port: urlObj.port || (protocol === 'wss' ? 443 : 80),
      path: path,
    });
    
    setupMQTTClient(client, connectionUrl);
    setMqttClient(client);
  };

  const setupMQTTClient = (client, url) => {
    client.on('connect', () => {
      console.log('✅ Connected to MQTT broker via WebSocket');
      console.log('📍 Connected URL:', url);
      if (device?.mqttTopic) {
        client.subscribe(device.mqttTopic, (err) => {
          if (err) {
            console.error('❌ Subscription error:', err);
          } else {
            console.log(`✅ Subscribed to topic: ${device.mqttTopic}`);
          }
        });
      } else {
        console.warn('⚠️ Device topic not available yet, will subscribe when device is loaded');
      }
    });

    client.on('error', (error) => {
      console.error('❌ MQTT connection error:', error);
      console.error('📍 Attempted URL:', url);
      console.error('💡 Tip: Check if MQTT WebSocket is enabled on the server');
    });

    client.on('offline', () => {
      console.log('⚠️ MQTT client went offline');
    });

    client.on('close', () => {
      console.log('🔌 MQTT connection closed');
    });

    client.on('reconnect', () => {
      console.log('🔄 Reconnecting to MQTT broker...');
    });

    client.on('message', (topic, message) => {
      setMessages(prev => [...prev, {
        topic,
        message: message.toString(),
        timestamp: new Date().toLocaleTimeString()
      }]);
    });

    setMqttClient(client);
  };

  const formatTime = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return '—';
    }
  };

  if (!device) {
    return <div className="loading">Loading device...</div>;
  }

  return (
    <div className="device-control-container">
      <div className="control-header">
        <button onClick={() => navigate('/devices')} className="back-btn">
          ← Back to Devices
        </button>
        <h1>{device.name}</h1>
        <div className="header-actions">
          <span className="user-name">{user?.email || user?.name || 'User'}</span>
          <button 
            onClick={() => {
              signOut();
              navigate('/login');
            }} 
            className="logout-btn"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="device-info">
        <div className="info-item">
          <label>Device ID:</label>
          <span>{device.deviceId}</span>
        </div>
        <div className="info-item">
          <label>Type:</label>
          <span>{device.type}</span>
        </div>
        <div className="info-item">
          <label>Connection:</label>
          <span className={`status ${device.status}`}>{device.status || 'unknown'}</span>
        </div>
        <div className="info-item">
          <label>RED line:</label>
          <span className={`line-state ${device.redState?.includes('not') ? 'not-run' : device.redState ? 'run' : 'na'}`}>
            {device.redState || 'ยังไม่มีข้อมูล'}
          </span>
        </div>
        <div className="info-item">
          <label>Green line:</label>
          <span className={`line-state ${device.greenState?.includes('not') ? 'not-run' : device.greenState ? 'run' : 'na'}`}>
            {device.greenState || 'ยังไม่มีข้อมูล'}
          </span>
        </div>
        <div className="info-item">
          <label>Last seen (data):</label>
          <span>{formatTime(device.lastSeen)}</span>
        </div>
        <div className="info-item">
          <label>LED Pin 4 (saved):</label>
          <span className={`led-saved-state ${device.ledPin4On ? 'on' : 'off'}`}>
            {device.ledPin4On ? 'ON' : 'OFF'}
            {device.ledPin4UpdatedAt && (
              <span className="led-saved-time"> · {formatTime(device.ledPin4UpdatedAt)}</span>
            )}
          </span>
        </div>
      </div>

      <div className="command-section">
        <h2>Quick commands</h2>
        <div className="command-examples">
          <h3>📋 Example Commands:</h3>
          <div className="example-buttons">
            <button
              type="button"
              onClick={async () => {
                const ledOn = Boolean(device.ledPin4On);
                const turnOn = !ledOn;
                const cmd = turnOn
                  ? { command: 'turn_on', pin: 4, value: true }
                  : { command: 'turn_off', pin: 4, value: false };
                try {
                  const { data } = await axios.post('/mqtt/publish', {
                    deviceId,
                    message: {
                      command: cmd.command,
                      pin: cmd.pin,
                      value: cmd.value
                    }
                  });
                  if (typeof data.ledPin4On === 'boolean') {
                    setDevice((prev) =>
                      prev
                        ? {
                            ...prev,
                            ledPin4On: data.ledPin4On,
                            ledPin4UpdatedAt: data.ledPin4UpdatedAt ?? prev.ledPin4UpdatedAt
                          }
                        : prev
                    );
                  }
                  if (data.warning) {
                    console.warn(data.warning);
                    alert(data.warning);
                  }
                  console.log('✅ Command sent:', cmd);
                } catch (error) {
                  console.error('Error sending command:', error);
                  alert('Failed to send command: ' + (error.response?.data?.error || error.message));
                }
              }}
              className={`example-btn example-btn-led-toggle ${device.ledPin4On ? 'led-is-on' : ''}`}
            >
              {device.ledPin4On ? '🔴 ปิด LED (Pin 4)' : '🔵 เปิด LED (Pin 4)'}
            </button>
            <button 
              onClick={async () => {
                const cmd = { command: 'register', pin: 0, value: false };
                try {
                  await axios.post('/mqtt/publish', {
                    deviceId,
                    message: {
                      command: cmd.command,
                      pin: parseInt(cmd.pin),
                      value: cmd.value
                    }
                  });
                  console.log('✅ Command sent:', cmd);
                } catch (error) {
                  console.error('Error sending command:', error);
                  alert('Failed to send command: ' + (error.response?.data?.error || error.message));
                }
              }}
              className="example-btn"
            >
              📋 Register Device
            </button>
            <button 
              onClick={async () => {
                const cmd = { command: 'info', pin: 0, value: false };
                try {
                  await axios.post('/mqtt/publish', {
                    deviceId,
                    message: {
                      command: cmd.command,
                      pin: parseInt(cmd.pin),
                      value: cmd.value
                    }
                  });
                  console.log('✅ Command sent:', cmd);
                } catch (error) {
                  console.error('Error sending command:', error);
                  alert('Failed to send command: ' + (error.response?.data?.error || error.message));
                }
              }}
              className="example-btn"
            >
              ℹ️ Get Device Info
            </button>
          </div>
        </div>
      </div>

      <div className="messages-section">
        <h2>Device Messages</h2>
        <div className="messages-list">
          {messages.length === 0 ? (
            <p className="no-messages">No messages yet...</p>
          ) : (
            messages.map((msg, index) => (
              <div key={index} className="message-item">
                <span className="message-time">{msg.timestamp}</span>
                <span className="message-topic">{msg.topic}</span>
                <span className="message-content">{msg.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default DeviceControl;

